import { AlertController } from '@ionic/angular';

/**
 * Aviso de «se cerrará sin guardar» para todas las ventanas (ion-modal) de la
 * app: un toque fuera del modal (el fondo), Escape, el botón atrás de Android o
 * deslizarlo hacia abajo lo cerraban sin preguntar y se perdía lo tecleado en
 * el picking o la recepción. Se instala una vez en AppComponent y se engancha
 * a cada modal cuando se presenta, sin tocar los componentes uno a uno.
 *
 * Solo pregunta si en el modal se ha tocado algo (escribir, elegir, marcar,
 * pulsar un botón del contenido). Cerrar por programa —al guardar, o al elegir
 * en un selector— no pregunta. Los modales de consulta o de búsqueda llevan
 * `data-cierre-libre` y se cierran siempre sin aviso.
 */
const EVENTOS_CAMBIO = ['ionInput', 'ionChange', 'input', 'change'];
const CLICS_QUE_CAMBIAN = 'ion-content ion-button, ion-content button, ion-content ion-item[button], ion-content ion-checkbox';
const ROLES_ACCIDENTALES = new Set(['backdrop', 'gesture']);

const sucios = new WeakMap<HTMLElement, boolean>();
let alertas: AlertController | null = null;
let preguntando: Promise<boolean> | null = null;

export function instalarAvisoCierre(controller: AlertController, doc: Document = document): void {
  alertas = controller;
  doc.addEventListener('ionModalWillPresent', (ev) => {
    const modal = ev.target as HTMLIonModalElement | null;
    if (!modal || modal.tagName !== 'ION-MODAL' || modal.hasAttribute('data-cierre-libre')) return;
    sucios.set(modal, false);
    if (modal.dataset['avisoCierre']) return;
    modal.dataset['avisoCierre'] = '1';

    const marcar = (): void => {
      sucios.set(modal, true);
    };
    EVENTOS_CAMBIO.forEach((tipo) => modal.addEventListener(tipo, marcar));
    modal.addEventListener('click', (e) => {
      if ((e.target as Element | null)?.closest?.(CLICS_QUE_CAMBIAN)) marcar();
    });
    // El resto de roles (sin rol, 'cancel', 'confirm'…) son cierres pedidos por
    // la propia ventana: el formulario ya decidió.
    modal.canDismiss = async (_data?: unknown, role?: string) =>
      !(role && ROLES_ACCIDENTALES.has(role)) || confirmarCierre(modal);
  });
}

/** ¿Se ha tocado algo en el modal que contiene a `el`? */
export function modalConCambios(el: Element | null): boolean {
  const modal = el?.closest('ion-modal') as HTMLElement | null;
  return !!modal && sucios.get(modal) === true;
}

/**
 * Pregunta antes de cerrar si hay cambios. `true` = se puede cerrar. Lo usa
 * también el botón Cancelar/× de los formularios (FormModalComponent).
 */
export async function confirmarCierre(el: Element | null): Promise<boolean> {
  if (!modalConCambios(el)) return true;
  const modal = el?.closest('ion-modal') as HTMLElement | null;
  const puede = await preguntar();
  if (puede && modal) sucios.set(modal, false);
  return puede;
}

function preguntar(): Promise<boolean> {
  if (!alertas) return Promise.resolve(true);
  // Dos toques seguidos en el fondo no abren dos avisos.
  preguntando ??= (async () => {
    const alerta = await alertas!.create({
      header: '¿Cerrar sin guardar?',
      message: 'Los cambios que has hecho en esta ventana se perderán.',
      backdropDismiss: false,
      buttons: [
        { text: 'Seguir aquí', role: 'cancel' },
        { text: 'Cerrar sin guardar', role: 'destructive' },
      ],
    });
    await alerta.present();
    const { role } = await alerta.onDidDismiss();
    return role === 'destructive';
  })().finally(() => {
    preguntando = null;
  });
  return preguntando;
}
